import { Button, LoadSpinner, TextArea } from '@calizahq/react-components-v2'
import { WorkspaceTab } from '@sales-app/context/workspace/workspace.type'
import { FollowMutations } from '@sales-app/graphql/mutations/follow.mutations'
import { TaskTypesQueries } from '@sales-app/graphql/queries/followup/tasktypes.queries'
import { Form, Formik } from 'formik'
import React, { FC } from 'react'

import { DateInputControlled, InputSelect, TimeInputControlled } from '@/components'
import { useGraphql, useGraphqlMutation } from '@/hooks/useGraphql'
import { formatDate } from '@/util'

import { formSchema, FormValuesType, initialValues } from './CreateFollowup.util'

type Props = {
  bookablePerson: WorkspaceTab
  refetchOnSuccess?: () => void
  toggle: () => void
}

interface Option {
  label: string
  value: number
}

export const CreateFollowup: FC<Props> = ({ toggle, bookablePerson, refetchOnSuccess }) => {
  const { data, isLoading } = useGraphql<TaskTypesQueries.GET_TASKTYPES.Response>(
    TaskTypesQueries.QUERY_KEY,
    TaskTypesQueries.getTaskTypes(),
  )

  const { mutate, isPending: isMutationLoading } = useGraphqlMutation(FollowMutations.createFollowUp(), {
    onSuccess: () => {
      refetchOnSuccess && refetchOnSuccess()
      toggle()
    },
  })

  const options: Option[] =
    data && data.followUpTaskTypes
      ? data.followUpTaskTypes.map((task) => {
          return { label: task.description, value: task.id }
        })
      : []

  const handleSubmit = (values: FormValuesType) => {
    const dueDate = formatDate(values.dueDate, 'yyyy-MM-dd')
    const dueTime = values.dueTime ? formatDate(values.dueTime, 'kk:mm:ss') : null
    mutate({
      followUpTask: {
        ...values,
        dueDate,
        dueTime,
        potentialBookingId: bookablePerson.potentialBookingId,
        completed: false,
      },
    })
  }
  return (
    <>
      {isLoading ? (
        <div className="flex h-48 w-full items-center justify-center ">
          <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
        </div>
      ) : (
        <Formik
          enableReinitialize={true}
          initialValues={{ ...initialValues }}
          onSubmit={handleSubmit}
          validateOnBlur={false}
          validationSchema={formSchema}
        >
          {() => {
            return (
              <Form className="flex w-full flex-col flex-wrap px-6 md:max-w-120">
                <p className="leading-nonemt-4 mb-4 font-bold text-header-md">Create a follow-up</p>
                <InputSelect
                  className="mb-4 w-full"
                  id="followUpTaskTypeId"
                  label="Action"
                  name="followUpTaskTypeId"
                  options={options}
                ></InputSelect>
                <TextArea className="mb-4 w-full" label="Notes" name="notes" />
                <div className="flex w-full flex-col md:mb-4 md:grid md:grid-cols-2 md:gap-4">
                  <DateInputControlled className="mb-4 w-full" label="Due Date" name="dueDate" />
                  <TimeInputControlled className="mb-4 w-full" label="Start Time" name="dueTime" />
                </div>
                <div className="flex w-full flex-col md:mb-4 md:grid md:grid-cols-2 md:gap-4"></div>
                <div className="flex flex-col-reverse justify-between md:flex-row">
                  <Button
                    className="w-full bg-action-secondary md:flex md:w-auto"
                    onClick={toggle}
                    size="small"
                    variant="secondary"
                  >
                    Close
                  </Button>
                  <Button
                    className="mb-2 w-full bg-text-semantic-success md:mb-0 md:w-auto"
                    disabled={isMutationLoading}
                    isLoading={isMutationLoading}
                    size="small"
                    type="submit"
                  >
                    Create Follow-up
                  </Button>
                </div>
              </Form>
            )
          }}
        </Formik>
      )}
    </>
  )
}
